import type { Module } from 'vuex';
import { getJob, updateJob, type GetJobResponse } from '../api/jobs.ts';
import RailsUtility from '../utilities/rails.ts';
import DateUtility from '../utilities/dates.ts';
import type { Contact } from '@/api/types/Contact.ts';

const emptyJob = {
  title: '',
  description: '',
  company: '',
  cities: [],
  created: '',
  updated: '',
  contacts: [],
  url: '',
  history: [],
};

const jobStore: Module<JobState, RootState> = {
  namespaced: true,
  state: {
    currentJob: emptyJob,
  },
  actions: {
    async loadCurrentJob({ commit, rootState }, { id }: { id: number }) {
      commit('general/showLoadingSpinner', null, { root: true });
      const data = await getJob({ id });
      const currentJob = parseJob(data);
      commit('setCurrentJob', { job: currentJob });
      commit('general/hideLoadingSpinner', null, { root: true });
    },
    flushCurrentJob({ commit }) {
      commit('setCurrentJob', { job: emptyJob });
    },
    async updateJob({ commit, dispatch, state }, { id, updatedJobAttributes }) {
      commit('general/showLoadingSpinner', null, { root: true });
      await updateJob({ id }, { updatedJobAttributes });
      if (state.currentJob.id === id) await dispatch('loadCurrentJob', { id });
      commit('general/hideLoadingSpinner', null, { root: true });
    },
  },
  mutations: {
    setCurrentJob(state, { job }) {
      state.currentJob = job;
    },
  },
};

const parseJob = (data: GetJobResponse) => {
  const activeJobDetails = data.record.job_details.find(detail => detail.status === 'active');
  const draftJobDetails = data.record.job_details.find(detail => detail.status === 'draft');
  const currentJob = {
    ...{
      id: data.record.id,
      indeedRefId: data.record.indeed_ref_id,
      jobType: data.record.job_type,
      status: RailsUtility.convertStatusFromApi(data.record.status),
      description: activeJobDetails?.description,
      title: data.record.title,
      url: data.record.url,
      //@ts-ignore
      userId: data.record.user_id,
      isStale: data.is_stale,
      draftJobDetails: data.draft_job_details || draftJobDetails,
      resumeRequired: data.record.resume_required,
    },
    jobStatus: RailsUtility.convertStatusFromApi(data.record.status),
    contacts: data.contacts,
    cities: data.record.job_locations,
  };

  const jobHistory = data.versions.reduce((history, version) => {
    const changes = version.version.object_changes;
    if (changes) {
      Object.entries(changes).forEach(([key, [from, to]]) => {
        if (!['updated_at', 'description', 'syndication_description'].includes(key)) {
          //@ts-ignore
          history.push({
            'updated by': version.whodunnit,
            'field changed': key.replace(/_/g, ' '),
            from,
            to,
            'updated at': DateUtility.formatDate(version.version.created_at, 'MMMM Do yyyy, h:mm a'),
          });
        }
      });
    }
    return history;
  }, []);
  //@ts-ignore
  currentJob.history = jobHistory;
  return currentJob;
};

export default jobStore;

export type JobHistory = {
  'updated by': string;
  'field changed': string;
  from: unknown;
  to: unknown;
  'updated at': string;
};

export type Job = {
  id?: number;
  indeedRefId?: string;
  jobType?: string;
  status?: string;
  jobStatus?: string;
  description: string;
  title: string;
  url: string;
  userId?: number;
  isStale?: boolean;
  draftJobDetails?: unknown;
  resumeRequired?: boolean;
  contacts: Contact[];
  cities: unknown[];
  history: JobHistory[];
};

export type JobState = {
  currentJob: Job;
};

export type RootState = {
  job: JobState;
};
